// API enums, mirroring the backend's common/constants/enums.ts: values stay English; their Indonesian labels live in
// locales/id/labels.ts, their tones in constants/tones.ts.

/**
 * `field_verifier`: visits a Hub applicant's site and fills the checklist (Phase 2). `courier`: delivers a Hub's orders
 * and records the exchange at the house (Phase 3; scope = one Hub).
 */
export type Role = 'admin' | 'viewer' | 'regulator' | 'operator' | 'field_verifier' | 'courier';
/** Every role, in the order the UI lists them (most access first). */
export const ROLES: readonly Role[] = ['admin', 'viewer', 'regulator', 'operator', 'field_verifier', 'courier'];

/**
 * What the signed-in person may do (GET /auth/me `permissions`): the single source of truth for menus and buttons.
 * The backend enforces the same keys on every route; the UI never re-derives them from the role.
 */
export const PERMISSIONS = [
  'overview.read', 'statistics.read', 'location.read', 'location.manage', 'location.photo.manage', 'cylinder.read', 'cylinder.register',
  'cylinder.update', 'cylinder.status', 'cylinder.retag', 'cylinder.test', 'cylinder.delete', 'cylinder.found', 'shipment.read', 'shipment.operate', 'gateway.read', 'gateway.manage', 'gateway.command',
  'gateway.command.admin', 'tag.read', 'event.read', 'alert.read', 'alert.ack', 'alert.close.any', 'ota.manage', 'audit.read', 'user.read', 'user.manage',
  'threshold.manage', 'pii.read', 'system.read',
  // Hub registration (Phase 2)
  'hub_application.read', 'hub_application.review', 'hub_application.approve', 'hub_application.visit', 'hub.sanction',
  // Households and orders (Phase 3)
  'household.read', 'household.pii.read', 'household.verify', 'household.manage', 'household.import', 'order.read', 'order.manage', 'order.deliver', 'order.fine_waive', 'program_setting.manage', 'hub_service_area.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * Where an account's access reaches: the whole country, one province (admin, viewer, regulator), or one location
 * (operator). Lists are forced into it by the backend; by-id answers outside it are a 404.
 */
export type Scope =
  | { kind: 'national' }
  | { kind: 'province'; provinceCode: string; provinceName: string }
  | {
      kind: 'location';
      locationId: number;
      locationName: string;
      provinceCode: string;
      provinceName: string;
      /** how many locations sit under this one in the distribution network (an operator reads them, acts only here) */
      networkSize?: number;
    };
export type ScopeKind = Scope['kind'];
export const OWNER_WAREHOUSE = 'OWNER_WAREHOUSE';
export const FILLING_STATION = 'FILLING_STATION';
export const HUB = 'HUB';
export const TEST_WORKSHOP = 'TEST_WORKSHOP';
/**
 * Distribution chain (backend migration 0030): Gudang Pusat (the one registration point) -> SPBG -> Hub (one or more
 * per village) -> households, plus the Bengkel Uji that withdrawals go to. In the chain's order (forms and filters
 * list them this way).
 */
export const LOCATION_TYPES = [OWNER_WAREHOUSE, FILLING_STATION, HUB, TEST_WORKSHOP] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];
/** DESTROYED is final: only a failed test at a Bengkel Uji sets it, and nothing about the cylinder changes after. */
export const CYLINDER_STATUSES = ['ACTIVE', 'WITHDRAWN', 'DAMAGED', 'LOST', 'DESTROYED'] as const;
export type CylinderStatus = (typeof CYLINDER_STATUSES)[number];
/** The statuses a person sets (POST /cylinders/:id/status); DESTROYED never. */
export const MANUAL_CYLINDER_STATUSES = ['ACTIVE', 'WITHDRAWN', 'DAMAGED', 'LOST'] as const satisfies readonly CylinderStatus[];
/** WITH_CONSUMER: held by a household (an exchange); WITH_COURIER: an empty on its way back to the Hub (Phase 3). */
export type PositionStatus = 'AT_LOCATION' | 'IN_TRANSIT' | 'MISSING' | 'WITH_CONSUMER' | 'WITH_COURIER';
/** What a cylinder holds, set by the server when a Surat Jalan departs with it (full from an SPBG to a hub). */
export const FILL_STATES = ['FULL', 'EMPTY'] as const;
export type FillState = (typeof FILL_STATES)[number];
/** A Surat Jalan's kind: along the chain, or a withdrawal to a Bengkel Uji (fixed at creation). */
export const SHIPMENT_KINDS = ['DISTRIBUTION', 'WITHDRAWAL'] as const;
export type ShipmentKind = (typeof SHIPMENT_KINDS)[number];
/** Why a cylinder is withdrawn: its periodic retest, or damage. */
export const WITHDRAWAL_REASONS = ['RETEST', 'DAMAGED'] as const;
export type WithdrawalReason = (typeof WITHDRAWAL_REASONS)[number];
/** A Bengkel Uji's verdict. */
export type TestResult = 'PASS' | 'FAIL';
export type ShipmentStatus = 'LOADING' | 'IN_TRANSIT' | 'RECEIVED' | 'RECEIVED_WITH_DISCREPANCY' | 'CANCELLED';
export type RejectReason = 'UNREGISTERED' | 'NOT_ACTIVE' | 'NOT_AT_ORIGIN' | 'ON_OTHER_SHIPMENT' | 'TEST_OVERDUE' | 'NOT_IN_SHIPMENT';
export type SessionKind = 'load' | 'unload';
export type Severity = 'critical' | 'warning' | 'info';
export type AlertType =
  | 'GATEWAY_OFFLINE'
  | 'READER_ERROR'
  | 'CLOCK_UNSYNCED'
  | 'TEST_DUE'
  | 'CLONE_SUSPECT'
  /** a chip bound to a cylinder read under another EPC: its EPC was rewritten (critical, never clears by itself) */
  | 'TAG_REWRITTEN'
  | 'SEEN_WITHOUT_SHIPMENT'
  | 'SHIPMENT_EXTRA_TAG'
  | 'SHIPMENT_MISSING'
  /** a Surat Jalan departed to a location outside the origin's line in the distribution network (allowed, alerted) */
  | 'SHIPMENT_OFF_NETWORK'
  | 'SEQ_GAP_UNRECOVERABLE'
  | 'BAD_PAYLOAD'
  // households, orders and the exchange (Phase 3)
  | 'STOCK_GAP'
  | 'ORDER_SLA_BREACHED'
  | 'ORDER_OVERCHARGED'
  | 'ORDER_CONFIRMATION_LOCKED'
  | 'EXCHANGE_FOREIGN_EMPTY'
  | 'EXCHANGE_MISMATCH'
  | 'HOUSEHOLD_HOARDING'
  | 'LOST_CYLINDER_SEEN'
  | 'HANDOVER_NOT_LEFT_HUB'
  | 'TAG_SUSPECTED_DEAD'
  | 'COURIER_MISMATCH_PATTERN'
  | 'EMPTY_NOT_RETURNED'
  | 'ORDER_CONFIRMED_WITHOUT_CODE'
  | 'DELIVERY_FAR_FROM_HOME'
  | 'COURIER_MULTI_DEVICE'
  | 'RETEST_OVERDUE_AT_HOUSEHOLD';
export type GatewayState = 'online' | 'offline' | 'unknown';
export type CommandStatus = 'sent' | 'ok' | 'error';
